import { useMutation } from "@tanstack/react-query";
import { useApiConfig } from "@visioni/a3s-client-api/app";
import { expandEndpoint } from "@visioni/a3s-client-api";
import { regenerateReportByPeriodEndpoint } from "@visioni/a3s-reports/client-messages";
import { isoDate } from "@visioni/a3s-codecs";
import { wasteSenseWeekStart } from "../wasteSenseWeekStart";

export interface RegenerateWasteSenseForWeekInput {
  /** An ISO-8601 instant inside the week to regenerate (the dump's timestamp). */
  readonly timestamp: string;
}

export interface UseRegenerateWasteSenseForWeekResult {
  /**
   * Fire-and-forget trigger. Resolves even on failure (including a 404 for a
   * week that has no report yet) — callers use it for background refresh and
   * must not surface an error to the user.
   */
  readonly regenerate: (input: RegenerateWasteSenseForWeekInput) => Promise<void>;
  readonly isPending: boolean;
}

/**
 * Fire-and-forget mutation that asks the backend to regenerate the WasteSense
 * report for the week containing a given instant (e.g. a just-confirmed dump),
 * so the report reflects the new dump without waiting for the next daily cron.
 *
 * It POSTs the by-period regenerate endpoint
 * (`…/reports/wastesense/period-start/{periodStart}/regenerate`) with an
 * `upToTimestamp` body identifying the dump's week. The backend rebuilds that
 * week's report choosing the most complete window server-side — up to *now* for
 * the current open week, or the whole Mon→Sun week if it's already closed — so a
 * dump reported at 3pm shows immediately rather than waiting for tomorrow's
 * tick, and a late dump can never shrink existing coverage. It also CREATES the
 * report if the week has none yet (e.g. a dump reported Mon 00:00–06:00 before
 * the first cron). Modelled on `useRegenerateRoadSenseLatest`.
 *
 * Best-effort by design: any error is swallowed (we never reject), so a caller
 * can `void regenerate(...)` right after a successful write without the regen
 * ever turning that write into a user-visible failure. A transient failure just
 * waits for the next daily recompute. The `periodStart` is computed client-side
 * (see {@link wasteSenseWeekStart}) to match the report row's key exactly.
 */
export function useRegenerateWasteSenseForWeek(): UseRegenerateWasteSenseForWeekResult {
  const apiConfig = useApiConfig();

  const mutation = useMutation<void, Error, RegenerateWasteSenseForWeekInput>({
    // Never retry: this is best-effort background work, so a transient failure
    // just waits for the next daily recompute rather than burning retries.
    retry: false,
    mutationFn: async ({ timestamp }) => {
      const { tenant, authenticatedFetch } = apiConfig;
      if (!tenant) {
        // Nothing to do without a resolved tenant. Return quietly rather than
        // throw — this is fire-and-forget.
        return;
      }

      // `periodStart` is the branded ISODate the endpoint's path schema expects;
      // `isoDate.parse` both brands and validates the YYYY-MM-DD shape.
      const periodStart = isoDate.parse(wasteSenseWeekStart(timestamp));
      const url =
        tenant.apiBaseUrl +
        expandEndpoint(regenerateReportByPeriodEndpoint, {
          path: { tenantId: tenant.tenantId, type: "wastesense", periodStart },
        });

      try {
        // POST with the dump's instant as `upToTimestamp` — it identifies the
        // week to regenerate; the backend picks the window (up to now for the
        // open week, whole week if closed) and creates the report if missing. We
        // deliberately ignore the response — this is fire-and-forget, and even a
        // non-2xx is not an error to surface to the operator.
        await authenticatedFetch(url, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ upToTimestamp: timestamp }),
        });
      } catch {
        // Swallow network/other errors: background refresh must never fail the
        // action that triggered it. The next daily cron self-heals the report.
      }
    },
  });

  return {
    regenerate: (input) => mutation.mutateAsync(input),
    isPending: mutation.isPending,
  };
}
